import Image from 'next/image';
import { type FC } from 'react';

import { type Optional } from 'utility-types';

import { AvatarDiscreteSize } from '@craft/ui/components/avatars';
import {
  type AvatarData,
  UserAvatar as NaiveUserAvatar,
  type UserAvatarProps as NaiveUserAvatarProps,
} from '@craft/ui/components/users';

import { NotClearedAvatarBadge } from '@/src/components/atoms/NotClearedAvatarBadge';
import { useHasFeatureFlag } from '@/src/hooks/featureFlags/useHasFeatureFlag';
import { useUserAvatar } from '@/src/hooks/query/users/use-user-avatar';

interface BaseUserAvatarProps extends Optional<
  Omit<
    NaiveUserAvatarProps,
    'avatar' | 'component' | 'email' | 'firstName' | 'fullName' | 'initials' | 'lastName'
  >,
  'alt'
> {
  readonly cleared?: boolean | null;
}

interface NameProperties {
  readonly firstName?: string;
  readonly fullName?: string;
  readonly initials?: string;
  readonly lastName?: string;
}

export interface UserAsyncAvatarProps extends BaseUserAvatarProps, NameProperties {
  readonly avatar?: never;
  readonly email?: never;
  /**
   * Whether the fetched avatar should include the blur placeholder. The placeholder carries a
   * per-user upstream fetch and payload cost, so it must be opted into explicitly by the few
   * renders where the progressive load is actually perceptible.
   *
   * @default false
   */
  readonly includeBlur?: boolean;
  readonly userId: string;
}

export interface UserClientSyncAvatarProps extends BaseUserAvatarProps, NameProperties {
  readonly avatar?: never;
  readonly email: string;
  readonly includeBlur?: never;
  readonly userId?: never;
}

export interface UserServerSyncAvatarProps extends BaseUserAvatarProps, NameProperties {
  /**
   * The server-resolved avatar, {@link AvatarData}, already fetched by the caller (typically as an
   * `avatar { url blurDataUrl }` selection on a wider query). Pass `null` when the data is still
   * loading or the user has no avatar; the initials fallback is rendered and no Gravatar request
   * is ever issued from the client.
   */
  readonly avatar: AvatarData | null;
  readonly email?: never;
  readonly includeBlur?: never;
  readonly userId?: never;
}

export type UserAvatarProps =
  | UserAsyncAvatarProps
  | UserClientSyncAvatarProps
  | UserServerSyncAvatarProps;

/**
 * Wraps the "@craft/ui" {@link NaiveUserAvatar} component to provide a user avatar whose data is
 * resolved in exactly one of three ways: fetched asynchronously by `userId`, supplied by the
 * caller as server-resolved {@link AvatarData}, or (legacy) probed client-side from an `email`.
 *
 * This component can safely be used as a direct replacement for the naive `UserAvatar` in contexts
 * where the avatar data may need to be fetched asynchronously, while still supporting scenarios
 * where the data is already available on the client or server.
 */
export const UserAvatar: FC<UserAvatarProps> = ({
  avatar,
  cleared,
  email,
  firstName,
  fullName,
  includeBlur = false,
  initials,
  lastName,
  size = AvatarDiscreteSize.Large,
  userId,
  ...props
}) => {
  const clearedFeatureEnabled = useHasFeatureFlag.ClearedStatusUx();

  /* Type coercion on userId is safe because the query's execution is paused if the userId is not
     defined. */
  const { data } = useUserAvatar(userId as string, size, {
    includeBlur,
    pause: userId === undefined,
  });

  return (
    <NotClearedAvatarBadge isEnabled={clearedFeatureEnabled && cleared === false}>
      <NaiveUserAvatar
        {...props}
        alt={props.alt ?? fullName ?? data?.fullName ?? 'no avatar available'}
        aria-label='profile picture'
        avatar={userId === undefined ? avatar : (data ?? null)}
        component={Image}
        email={email}
        firstName={firstName}
        fullName={data?.fullName ?? fullName}
        initials={data?.initials ?? initials}
        lastName={lastName}
        size={size}
      />
    </NotClearedAvatarBadge>
  );
};
